import React, {Component} from 'react';
import moment from 'moment';

import classNames from 'classnames';
import strings from 'utils/strings';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPrint} from '@fortawesome/free-solid-svg-icons';

const childTypeGrades = [
	'child-screening',
	'child',
	'youth-screening',
	'youth-followup',
];

const NOT_AVAILABLE = strings.common.inteTillganglig;

/**
 * Safe getter for nested values.
 * Example:
 * get(testUser, 'education.youthGrade')
 */
const get = (object, path, fallback) => {
	if (!object || !path) {
		return fallback;
	}

	const value = path.split('.').reduce((current, key) => {
		if (current && Object.prototype.hasOwnProperty.call(current, key)) {
			return current[key];
		}

		return undefined;
	}, object);

	return value === undefined || value === null ? fallback : value;
};

const hasValue = (value) => {
	return value !== undefined && value !== null && value !== '';
};

const getContent = (props) => {
	if (hasValue(props.children)) {
		return props.children;
	}

	if (hasValue(props.value)) {
		return props.value;
	}

	return NOT_AVAILABLE;
};

const formatBool = (value, hasLongIntro) => {
	if (
		value === 'yes' ||
		value === 'ja' ||
		value === true ||
		value === 'true'
	) {
		return strings.common.ja;
	}

	if (
		value === 'no' ||
		value === 'nei' ||
		value === false ||
		value === 'false'
	) {
		return strings.common.nej;
	}

	if (value === '') {
		return strings.common.inteTillganglig;
	}

	if (hasLongIntro && typeof value === 'undefined') {
		return strings.common.inteTillganglig;
	}

	if (hasLongIntro && value) {
		return strings.common.ja;
	}

	if (hasLongIntro && !value) {
		return strings.common.nej;
	}

	if (!hasLongIntro && !value) {
		return strings.common.nej;
	}

	return '';
};

const formatAge = (age) => {
	const years = get(age, 'years');
	const months = get(age, 'months');

	if (!hasValue(years) && !hasValue(months)) {
		return NOT_AVAILABLE;
	}

	if (hasValue(years) && hasValue(months)) {
		return strings.formatString(strings.common.arOchManader, years, months);
	}

	if (hasValue(years)) {
		return strings.formatString(strings.common.arFormat, years);
	}

	return strings.formatString(strings.common.manaderFormat, months);
};

const formatGender = (gender, testType) => {
	const isChildType = childTypeGrades.includes(testType);

	if (gender === 'm' && isChildType) {
		return strings.common.boy;
	}

	if (gender === 'f' && isChildType) {
		return strings.common.girl;
	}

	if (gender === 'm' && !isChildType) {
		return strings.common.male;
	}

	if (gender === 'f' && !isChildType) {
		return strings.common.female;
	}

	return NOT_AVAILABLE;
};

const formatDate = (date) => {
	if (!date) {
		return NOT_AVAILABLE;
	}

	const parsedDate = moment(date);

	if (!parsedDate.isValid()) {
		return NOT_AVAILABLE;
	}

	return parsedDate.format('LL');
};

const InfoRow = (props) => {
	if (props.condition !== undefined && !props.condition) {
		return null;
	}

	return (
		<p className="info__row">
			<span className="info__label">{props.name}</span>
			<span className="info__value">{getContent(props)}</span>
		</p>
	);
};

const InfoRowBool = (props) => {
	const value = hasValue(props.children) ? props.children : props.value;
	const valueText = formatBool(value, props.hasLongIntro);

	return (
		<InfoRow condition={props.condition} name={props.name}>
			{valueText || NOT_AVAILABLE}
		</InfoRow>
	);
};

class TestUserInfo extends Component {
	render() {
		const testResult = this.props.testResult || {};

		/*
			Normally the data is expected as:
			testResult.testUser

			This fallback also allows the component to survive if it receives
			a partial user object directly, for example:
			{ education: { youthGrade: 6 } }
		*/
		const testUser = testResult.testUser || testResult || {};

		const testType = this.props.testType;
		const hasLongIntro = !!testResult.anamnese;
		const isTestOwner = !!testResult.isTestOwner;

		const firstName = get(testUser, 'firstName', '');
		const lastName = get(testUser, 'lastName', '');
		const email = get(testUser, 'email', '');
		const phone = get(testUser, 'phone', '');

		const completeName = firstName
			? `${firstName} ${lastName || ''}`.trim()
			: strings.common.hiddenname;

		const completeEmail = firstName && email ? email : 'Inte tillgänglig';

		const youthGrade = get(testUser, 'education.youthGrade');
		const isInHighSchool = get(testUser, 'education.isInHighSchool');

		const age = get(testUser, 'age', {});
		const gender = get(testUser, 'gender');

		const completedAt = get(testResult, 'completedAt');
		const hasCompletedAt = completedAt && moment(completedAt).isValid();

		return (
			<div>
				<div className="lit-input-information">
					<button
						className={classNames('print-hide', {
							'lit-btn2 see-more': true,
							'bg-tertiary': true,
						})}
						onClick={() => {
							window.print();
							/* this.updatePrintLog('Results'); */
						}}
					>
						<FontAwesomeIcon
							size="lg"
							className="lit-icon-btns"
							icon={faPrint}
						/>

						<p className="lit-btns-txt">
							{strings.testView.printInformation}
						</p>
					</button>
				</div>

				<div className="info">
					<InfoRow
						condition={isTestOwner}
						name={strings.common.name}
						hasLongIntro={hasLongIntro}
					>
						{completeName}
					</InfoRow>

					{testType === 'child-screening' && (
						<InfoRow
							condition={
								hasValue(youthGrade) || isInHighSchool === true
							}
							hasLongIntro={hasLongIntro}
							name={strings.common.arskurs + ': '}
						>
							{hasValue(youthGrade) ? youthGrade : NOT_AVAILABLE}
						</InfoRow>
					)}

					<InfoRow
						condition={hasCompletedAt}
						name={strings.testView.datumForTestgenomforandet + ': '}
						value={formatDate(completedAt)}
					/>

					<InfoRow
						condition={isTestOwner}
						name="E-post:"
						value={completeEmail}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						condition={isTestOwner}
						name="Telefon:"
						value={phone || NOT_AVAILABLE}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						name={strings.testView.alderVidTestgenomforandet + ':'}
						value={formatAge(age)}
						hasLongIntro={hasLongIntro}
					/>

					<InfoRow
						name={strings.common.kon + ':'}
						value={formatGender(gender, testType)}
						hasLongIntro={hasLongIntro}
					/>
				</div>
			</div>
		);
	}
}

export default TestUserInfo;
